import React from "react";
import { useGetPositionsOfPaymentPrefQuery } from "../../../../../graphql/supergraph.index";
import { type PositionsPaginatedType, type PositionNodeType } from "../../../common/types";
import { FETCH_MORE_POSITIONS_PER_PAGE, INITIAL_POSITIONS_PER_PAGE } from "../constants";
import { PositionListBaseComponent } from "../PositionListBaseComponent";
import { getPositionsPaginated } from "./utils";

export type PositionListOfPaymentPrefProps = {
  id: string;
  getHeader?: (positionsPaginated: PositionsPaginatedType) => JSX.Element;
  showPositions?: boolean;
  linkifyPosition?: (position: PositionNodeType) => string;
};

export const PositionListOfPaymentPref: React.FC<PositionListOfPaymentPrefProps> = ({
  id,
  getHeader,
  showPositions = true,
  linkifyPosition,
}: PositionListOfPaymentPrefProps) => {
  const {
    data,
    fetchMore: positionListFetchMore,
    error: positionListError,
    loading: positionListLoading,
  } = useGetPositionsOfPaymentPrefQuery({
    variables: {
      id,
      first: INITIAL_POSITIONS_PER_PAGE,
    },
    fetchPolicy: "cache-and-network",
  });

  const positionsPaginated = getPositionsPaginated(data);
  const handleLoadMorePositions = () => {
    if (!positionsPaginated?.pageInfo.hasNextPage) {
      return;
    }
    void positionListFetchMore({
      variables: {
        id,
        first: FETCH_MORE_POSITIONS_PER_PAGE,
        after: data?.paymentPrefs?.[0].positionsPaginated?.pageInfo.endCursor,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        if (!fetchMoreResult?.paymentPrefs?.[0].positionsPaginated) {
          return previousResult;
        }

        if (!previousResult.paymentPrefs?.[0].positionsPaginated) {
          return fetchMoreResult;
        }

        return {
          ...previousResult,
          paymentPrefs: [
            {
              ...previousResult.paymentPrefs[0],
              id: previousResult.paymentPrefs[0].id,
              positionsPaginated: {
                ...previousResult.paymentPrefs[0].positionsPaginated,
                edges: [
                  ...(previousResult?.paymentPrefs?.[0].positionsPaginated?.edges ?? []),
                  ...fetchMoreResult.paymentPrefs[0].positionsPaginated.edges,
                ],
                pageInfo: fetchMoreResult.paymentPrefs[0].positionsPaginated.pageInfo,
              },
            },
          ],
        };
      },
    });
  };

  return (
    <>
      {positionsPaginated && getHeader?.(positionsPaginated)}
      {showPositions && (
        <PositionListBaseComponent
          positionListError={positionListError}
          positionListLoading={positionListLoading}
          positionsPaginated={positionsPaginated}
          fetchMorePositions={handleLoadMorePositions}
          linkifyPosition={linkifyPosition}
        />
      )}
    </>
  );
};
